import React from "react";

const SubmittingAnArticle = () => {
  return (
    <React.Fragment>
      <h1>Submitting an Article</h1>
      <p>
        CJoE follows a merit-based publication system, ensuring that only articles meeting its
        editorial standards and containing no fraudulent or harmful science are published. CJoE is
        an open-access platform with zero article processing charges (APCs) and minimal or no
        editing fees for qualifying manuscripts. It offers quality peer review, inclusive
        publishing, and a user-friendly submission interface. Authors benefit from trusted partners
        like Google Scholar, Crossref, and Dimensions while easily tracking research engagement
        through comments, claps, views, downloads, and SIQ™ scores. Authors must complete the
        following steps to submit an article draft for peer review. Authors may save and exit the
        submission process at any time by clicking “Exit” in the top right corner of the page.
      </p>

      <div className="big-numbers">
        <div className="step-1">
          <h3>Getting started</h3>
          <p>
            Are you submitting on behalf of a channel? Designate your article as a channel here,
            then choose the article type. This information cannot be changed after moving on to the
            Title, etc. section.
          </p>
        </div>

        <div className="step-2">
          <h3>Title, etc.</h3>

          <h4>Title</h4>
          <div className="submission-step-info stack-small">
            <div className="left-side">
              <div className="space-bottom-s">
                <i className="fas fa-check blue"></i>
                <div className="icon-before blue small-font">
                  <strong>APA style</strong>
                </div>
              </div>
              <div className="space-bottom-s">
                <i className="fas fa-check blue"></i>
                <div className="icon-before blue small-font">
                  <strong>Title case</strong>
                </div>
              </div>
              <div>
                <i
                  className="fas fa-check blue"
                  style={{ position: "relative", bottom: "15px" }}
                ></i>
                <div className="icon-before blue small-font">
                  <strong>
                    Only one <br /> sentence
                  </strong>
                </div>
              </div>
            </div>
            <div className="right-side">
              <div className="space-bottom-s">
                <div className="icon-with-text">
                  <div className="icon">
                    <i className="fas fa-exclamation-triangle red"></i>
                  </div>
                  <div className="text small-font">
                    <strong>
                      Do not enter the title in all caps or include a period at the end.
                    </strong>
                  </div>
                </div>
              </div>
              <div className="space-bottom-s">
                <div className="icon-with-text">
                  <div className="icon">
                    <i className="fas fa-exclamation-triangle red"></i>
                  </div>
                  <div className="text small-font">
                    <strong>
                      Acronyms, initialisms, or abbreviations are not permitted in the article
                      title.
                    </strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p>
            Beginning with this step, you may preview your draft at any time to see how the
            published version will be displayed.
          </p>

          <h4>
            <strong>Abstract</strong>
          </h4>
          <div className="submission-step-info stack-small">
            <div className="left-side">
              <div className="inline-block-small medium-font blue space-bottom-xxs">
                <strong>3500</strong>
              </div>
              <div className="inline-block-small small-font blue">
                <strong>characters limit</strong>
              </div>
            </div>
            <div className="right-side">
              <div className="icon-with-text">
                <div className="icon">
                  <i className="fas fa-exclamation-triangle red"></i>
                </div>
                <div className="text small-font">
                  <strong>Abstract subheaders are permitted in original articles only.</strong>
                </div>
              </div>
            </div>
          </div>

          <h4>
            <strong>Categories</strong>
          </h4>
          <div className="submission-step-info stack-small">
            <div className="left-side only">
              <div className="inline-block-small medium-font blue space-bottom-xxs">
                <strong>1-3</strong>
              </div>
              <div className="inline-block-small small-font blue">
                <strong>article categories</strong>
              </div>
            </div>
            <div className="right-side"></div>
          </div>

          <h4>
            <strong>Keywords</strong>
          </h4>
          <div className="submission-step-info stack-small">
            <div className="left-side">
              <div className="inline-block-small medium-font blue space-bottom-xxs">
                <strong>5-10</strong>
              </div>
              <div className="inline-block-small small-font blue">
                <strong>keywords</strong>
              </div>
            </div>
            <div className="right-side">
              <div className="icon-with-text">
                <div className="icon">
                  <i className="fas fa-info-circle blue"></i>
                </div>
                <div className="text small-font">
                  <strong>
                    Please keep your keywords short (a maximum of 3-4 words each) and as specific as
                    possible.
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="step-3">
          <h3>Authors</h3>
          <h4>
            <strong>List</strong>
          </h4>
          <p>
            Add your co-authors. Authors cannot be added after submission, so make sure to include
            all authors before submission. The submitting author is listed as the first author by
            default; this can be changed by dragging and dropping the authors into the desired
            order.
          </p>

          <p>
            <strong>
              The submitting author is responsible for editing all author names and affiliations.
            </strong>
            While co-authors can edit their name and affiliation in their Cureus Journal profiles,
            all such changes will not be reflected in articles submitted to or published priorly in
            Cureus Journals. Author names and affiliations for all authors listed in an article must
            be accurately filled and confirmed by the submitting author at the time of submission.
          </p>

          <p>
            <strong>Affiliations: </strong>Every author is required to provide an accurate and
            consistently formatted affiliation name and location. CJoE contributors such as students
            and researchers should specify their affiliation and identify the relevant article
            category based on their specialization (e.g., Civil Engineering, Mechanical Engineering,
            Chemical Engineering, etc.). Titles such as Professor, Assistant/Associate Professor,
            Research Scholar, Student, Department Chair, etc., should not be included in the author
            affiliation and must be omitted from the specialization field. When two or more authors
            are from the same institution, the institution's name must be entered identically for
            all such authors. Additionally, details in the department field, including spelling,
            capitalization, spacing, etc., should also match precisely.
          </p>

          <p>
            Articles submitted with inconsistently formatted affiliations will be corrected before
            publication. However, additional editing fees may apply. Therefore, it is crucial to
            ensure that all author affiliations are uniform from the beginning.
          </p>

          <h4>
            <strong>Contributions</strong>
          </h4>
          <p>
            All authors must meet the{" "}
            <a href="/journal/business-and-economics/guide#!/policies-and-procedures/icmje-requirements">
              ICMJE guidelines
            </a>{" "}
            for authorship to be listed as an author. Open the dropdown menu next to each author to
            select their contributions to the work. Authors who do not satisfy ICMJE authorship
            criteria should be removed from the author list and mentioned in the Acknowledgements
            section (consent needed).
          </p>
        </div>

        <div className="step-4">
          <h3>Disclosures</h3>
          <p>
            Please provide all relevant information on any human or animal subjects used in the
            study and disclose any relevant conflicts of interest (COI). You may also add
            acknowledgements at this time.
          </p>
          <p>
            If human or animal subjects are included in the study, please provide the relevant
            institutional review board (IRB) names and approval numbers. Also, please confirm
            whether informed consent was obtained or waived by IRB for volunteering human subjects.
          </p>
          <p>
            The submitting author is responsible for including co-author COI and disclosures. Are
            you unsure about potential COI among your co-authors? You can send an email request for
            this information directly to your co-authors from inside the submission process.
          </p>
          <p>
            COI may involve third-party payment or service for any aspect of the submitted work,
            relevant financial activities outside the submitted work, patents that could be
            considered broadly relevant to the work, and any relationship(s) not covered previously.
          </p>
          <p>
            Based on the information provided and disclosures, CJoE will automatically generate an
            Ethics Statement and COI Disclosure to be included alongside the published article.
            Please see the{" "}
            <a
              href="/journal/engineering/guide#!/policies-and-procedures/single-blinded-peer-review"
              target="_blank"
              rel="noopener"
            >
              Policies and Procedures
            </a>{" "}
            and{" "}
            <a
              href="/journal/engineering/guide#!/policies-and-procedures/conflicts-of-interest"
              target="_blank"
              rel="noopener"
            >
              Competing Interests
            </a>{" "}
            sections of the Author Guide to view a detailed breakdown of required statements and
            disclosures.
          </p>
        </div>

        <div className="step-5">
          <h3>Article</h3>
          <p>
            Enter the main article content here. Article sections will vary depending on the article
            type selected earlier in the submission process. Do not compose your article directly in
            the text field. Instead, draft your article in the word processor of your choice, and
            then copy and paste your article text into the appropriate section. Please do not
            include section names (e.g. Introduction, Materials and Methods, Results, etc.) as these
            are added automatically.
          </p>

          <h4>
            <strong>Previously Presented at Conference as Abstract or Poster</strong>
          </h4>
          <p>
            If you previously presented your article as an abstract or poster, this must be
            mentioned in the acknowledgments section. Please include the meeting or conference title
            and date. For example, “This article was previously presented as a meeting abstract at
            the 2020 AANS Annual Scientific Meeting on April 27, 2020.”
          </p>

          <h4>
            <strong>Previously Posted to Preprint Server </strong>
          </h4>
          <p>
            If you previously posted your article to a preprint server, this must be mentioned in
            the acknowledgments section. For example, “This article was previously posted to the
            Crossref preprint server on April 27, 2020.” Article submissions currently available on
            a preprint server will be accepted if they are not pending full publication elsewhere.
          </p>

          <h4>
            <strong>Text Formatting</strong>
          </h4>
          <p>
            All articles must adhere to <strong>Cureus formatting styles:</strong>
          </p>
          <ul>
            <li className="check">
              <strong>No paragraph indentations or bulleted lists</strong>
            </li>
            <li className="check">
              <strong>Only one line return after each paragraph</strong>
            </li>
            <li className="check">
              <strong>Only one space between sentences </strong>
            </li>
            <li className="warning">
              <strong>
                Please remove any text styling before copying and pasting your text into the
                appropriate field
              </strong>
            </li>
          </ul>

          <h4>
            <strong>Conclusions</strong>
          </h4>
          <p>
            The conclusions should be concise and exclude any data or other information requiring
            citations.
          </p>
          <div className="submission-step-info stack-small">
            <div className="left-side">
              <div className="inline-block-small medium-font blue space-bottom-xxs">
                <strong>1-2</strong>
              </div>
              <div className="inline-block-small small-font blue">
                <strong>paragraphs</strong>
              </div>
            </div>
            <div className="right-side">
              <div className="icon-with-text">
                <div className="icon">
                  <i className="fas fa-exclamation-triangle red"></i>
                </div>
                <div className="text small-font">
                  <strong>
                    Articles with conclusions that are just a few sentences long will not be
                    accepted.
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <h4>
            <strong>Numbers</strong>
          </h4>
          <p>
            Numbers 1-9 should be spelled out except in cases of measurement (e.g., 7 mm). Numbers
            higher than nine should be entered numerically (10, 100, 463, 1,932, etc.)
          </p>

          <h4>
            <strong>Citations</strong>
          </h4>
          <p>
            References should be cited using square brackets. Square brackets should not be used
            anywhere in your article except for the in-text citations. Reference citations may be
            placed mid-sentence or at the end of a sentence (but before the period). If multiple
            references are cited in one place, please list them as follows: [1-4], [1,3], [5,9-12],
            etc. Footnotes are not permitted. Citations are not permitted in figures, tables,
            videos, or 3D model titles. They should instead be included in the media legend or table
            cells.
          </p>

          <p>
            All submitted manuscripts should cite only relevant and appropriate literature to
            support the claims made. Excessive self-citation, coordinated efforts among authors to
            collectively self-cite, gratuitous or unnecessary citation of articles published in the
            journal to which the paper has been submitted, and any form of citation manipulation are
            considered inappropriate activities.
          </p>

          <p>
            Citation manipulation may lead to article rejection and could be reported to the
            authors' institutions. Similarly, any attempts by peer reviewers and/or editors to
            encourage such practices should be reported to the respective publisher.
          </p>

          <p>Authors should follow these guidelines when preparing their manuscript: </p>
          <ul className="indented-for-medium-up disc">
            <li>
              Any statement relying on external sources (i.e., not the authors' novel ideas/findings
              or general knowledge) should include a citation.
            </li>
            <li>
              Authors should avoid citing derivations of original work. For example, they should
              cite the original work rather than a review article that cites it.
            </li>
            <li>
              Citations should be accurate and directly support the statement made in the
              manuscript. Authors must not cite any work that does not support their claim(s).
            </li>
            <li>Authors should only cite sources they have read.</li>
            <li>
              Authors should preferentially not cite their own, their friends', peers', or
              institution's publications.
            </li>
            <li>Authors should avoid citing work solely from one country.</li>
            <li>Authors should not use excessive citations to support a single point.</li>
            <li>Ideally, authors should only cite peer-reviewed works.</li>
            <li>Authors should not cite advertisements or advertorial material.</li>
          </ul>

          <h4>
            <strong>Media (figures/tables/videos/interactive models)</strong>{" "}
          </h4>
          <p>
            To add media to your article, first place your cursor in the desired location in the
            article section text field and click one of the Insert Figure, Table, Video, or 3D model
            buttons on the article section toolbar. You will then be prompted to upload the figure,
            paste or create the table, or paste the video or 3D model URL and add a title and
            optional legend.
          </p>
          <p>
            To learn more about media prerequisites (dimensions, size, quality,
            acceptable/unacceptable media types, etc.), please refer to the{" "}
            <a href="/media_guide#!/overview/adding-media" target="_blank" rel="noopener">
              Media Guide
            </a>
            .
          </p>
          <div className="submission-step-info stack-small">
            <div className="left-side">
              <div className="inline-block-small medium-font blue space-bottom-xxs">
                <strong>25</strong>
              </div>
              <div className="inline-block-small small-font blue">
                <strong>media items per article limit</strong>
              </div>
            </div>
            <div className="right-side">
              <div className="icon-with-text">
                <div className="icon">
                  <i className="fas fa-exclamation-triangle red"></i>
                </div>
                <div className="text small-font">
                  <strong>
                    Media is not permitted in the abstract or conclusions section. The only
                    exception is the introduction section of a review article.
                  </strong>
                </div>
              </div>
            </div>
          </div>

          <h4>
            <strong>Subheaders</strong>
          </h4>
          <p>CJoE allows for three levels of subheaders.</p>
          <p>
            Major subheaders should be formatted using the subheader style found in the “Styles”
            dropdown menu. Major subheaders should be in sentence case, i.e. the first word should
            be capitalized, and all other words should be in lowercase, except proper nouns and
            acronyms/initialisms (such as GBM, SRS, etc.).
          </p>
          <p>
            Minor subheaders should be in the title case and italicized. Both major and minor
            subheaders should be followed by a line return before the next paragraph begins. If the
            article contains a third level of subheader, it should be in sentence case and lead off
            the paragraph, followed by a colon. E.g. Test 1: This test exhibited signs of…
          </p>

          <h4>
            <strong>Formulas</strong>
          </h4>
          <p>
            CJoE supports LaTex. Formulas may be built using LaTeX during your submission or you may
            copy/paste and convert your pre-prepared formulas. Please keep this in mind when
            preparing your manuscript and explore the{" "}
            <a href="/equation_guide" target="_blank" rel="noopener">
              Equations Guide
            </a>{" "}
            too.
          </p>
          <h4>
            <strong>Appendices (Optional)</strong>
          </h4>
          <p>Add supplemental information here.</p>

          <h4>
            <strong>Miscellaneous</strong>
          </h4>
          <p>
            Please{" "}
            <a
              href="https://www.ibm.com/support/pages/how-cite-ibm-spss-statistics-or-earlier-versions-spss"
              target="_blank"
              rel="noreferrer noopener"
            >
              refer here for information on how to cite IBM SPSS statistics
            </a>{" "}
            or{" "}
            <a
              href="https://www.stata.com/support/faqs/resources/citing-software-documentation-faqs/"
              target="_blank"
              rel="noreferrer noopener"
            >
              Stata software
            </a>
          </p>
        </div>

        <div className="step-6">
          <h3>References</h3>
          <p>
            Creating references is simple with our automated converting tool. Please format all
            references in a text document according to the Cureus style before copying and pasting
            the list and selecting "Convert references."
          </p>
          <p>
            Once all references have been converted, please preview (and edit as necessary) each
            reference to ensure that it has been converted correctly. We will do our best to detect
            each reference type based on its formatting, but the conversion tool is not perfect, and
            the results will require your review. When editing your converted references, please do
            not add punctuation at the end; punctuation will be added automatically. If you prefer,
            you can also enter your references one by one.
          </p>
          <p>
            Please view the <a href="#!/author-instructions/references">References section</a> for
            more detailed instructions.
          </p>
        </div>

        <div className="step-7">
          <h3>Reviewers</h3>

          <h4>
            <strong>Author-Invited Reviewers</strong>
          </h4>
          <p>
            The submitting author must suggest a minimum of five individuals with requisite domain
            knowledge to review the article. Please note that engineering students may not be
            invited to peer review. Before the forwarding of a review invitation, each
            author-submitted reviewer is carefully vetted by CJoE editorial staff. Reviewers who are
            deemed unsuitable are replaced by CJoE editorial staff at this time.
          </p>

          <h4>
            <strong>CJoE-Invited Reviewers </strong>
          </h4>
          <p>
            In addition to these author-suggested reviewers, a minimum of six peer reviewers are
            automatically invited from the CJoE internal peer review panel. They include relevant
            specialists who have either volunteered for the panel or previously published as an
            author in CJoE. This ensures that at least 11 peer reviewers are invited to review each
            article. Once the article has entered peer review, three additional reviewers are
            automatically invited by CJoE every two days to ensure that the article completes peer
            review in a timely fashion.
          </p>

          <h4>
            <strong>Satisfying Peer Review</strong>
          </h4>
          <p>
            Two completed reviews from CJoE-invited reviewers are required to satisfy peer review.
            CJoE editors will reject reviews that do not include constructive feedback.
          </p>
        </div>

        <div className="step-8">
          <h3>Summary</h3>
          <p>
            The summary contains a section-by-section outline of your article. You can review each
            aspect of your article and jump directly to a specific section of the submission process
            if you wish to make changes.
          </p>
          <p>
            If any issues with the reference citations are detected, you will receive a notification
            and a list of all the citation- and/or reference-related issues (e.g. missing citations,
            missing references, incorrectly formatted citations). It is your responsibility to
            identify and address all listed issues to proceed to the next step and submit your
            article.
          </p>
        </div>

        <div className="step-9">
          <h3>Submit</h3>
          <p>
            Please preview your article draft one last time and make sure everything looks good. You
            cannot submit your draft without first previewing your work. When you are satisfied,
            click the ‘Submit my article’ button. Peer review invitation emails will be sent out and
            the peer review process will begin when your article is approved during our preliminary
            editorial review.
          </p>
        </div>

        <div className="step-10">
          <h3>
            Purchasing Preferred Editing Service or Uploading Third Party Editing Service Receipt
          </h3>
          <p>
            If, after submission, you receive an email stating that our{" "}
            <a href="/cureus_editing_services" target="_blank" rel="noopener">
              Preferred Editing Service
            </a>{" "}
            is required, you will be prompted to enter your payment information and resubmit the
            article. Your article cannot be edited while deferred for Preferred Editing.
          </p>
          <p>
            If, after submission, you receive an email stating that professional third-party
            language editing is required, you will be prompted to enter the name of the service and
            upload an invoice or receipt when re-submitting the article.
          </p>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SubmittingAnArticle;
